import React, { useEffect, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { SerialService, parseSerials } from '../../services/serial.service';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

export interface SerialNumbersFieldProps {
  /** capture: new serials typed/scanned/pasted; pick: chosen from the store's IN_STOCK serials. */
  mode: 'capture' | 'pick';
  /** Units on the line; the field shows "n of expected". */
  expected: number;
  value: string[];
  onChange: (serials: string[]) => void;
  itemId?: string | null;
  /** pick: the store to list IN_STOCK serials from. */
  storeId?: string | null;
  /** pick: fixed choices instead of the store list. */
  options?: string[];
  /** pick: offer the serials of this item SOLD on this challan (sales return) instead of the store list. */
  soldOnChallanId?: string | null;
  label?: string;
  /** When serial tracking of the item is unknown: the field is shown but may be left empty. */
  optional?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * F6b: one serial number per unit. A multi-value box (MUI Autocomplete; the app's SearchableSelect is single-value):
 * type or scan one serial and press Enter, or paste a list separated by new lines, commas or semicolons.
 */
const SerialNumbersField: React.FC<SerialNumbersFieldProps> = ({
  mode, expected, value, onChange, itemId, storeId, options, soldOnChallanId, label, optional, disabled, 'data-testid': testId,
}) => {
  const canView = usePermission(PERMISSIONS.STOCK_VIEW);
  const [available, setAvailable] = useState<string[]>([]);
  const [input, setInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    const institutionId = AuthService.getSelectedInstitution();
    if (mode !== 'pick' || options || !canView || !institutionId || !itemId || (!storeId && !soldOnChallanId)) {
      setAvailable([]);
      return undefined;
    }
    (soldOnChallanId
      ? SerialService.soldOnChallan(institutionId, soldOnChallanId, itemId)
      : SerialService.available(institutionId, storeId!, itemId))
      .then((rows) => { if (!cancelled) setAvailable(rows.map((r) => r.serialNo)); })
      .catch(() => { if (!cancelled) setAvailable([]); });
    return () => { cancelled = true; };
  }, [mode, options, canView, storeId, itemId, soldOnChallanId]);

  const add = (more: string[]) => {
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const next = [...value];
    more.forEach((s) => {
      if (!seen.has(s.toLowerCase())) { seen.add(s.toLowerCase()); next.push(s); }
    });
    onChange(next);
  };

  const count = value.length;
  const mismatch = count > 0 ? count !== expected : !optional && expected > 0;
  const helper = optional && count === 0
    ? 'Only needed if this item is serial-tracked'
    : `${count} of ${expected} serial number${expected === 1 ? '' : 's'}${mode === 'pick' ? ' picked' : ' entered'}`;

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      disabled={disabled}
      options={options ?? available}
      value={value}
      inputValue={input}
      filterSelectedOptions
      onInputChange={(_, v, reason) => {
        if (reason === 'input' && /[\n,;]/.test(v)) {
          add(parseSerials(v));
          setInput('');
        } else {
          setInput(v);
        }
      }}
      onChange={(_, v) => onChange(Array.from(new Set((v as string[]).map((s) => s.trim()).filter(Boolean))))}
      renderTags={(tags, getTagProps) => tags.map((t, i) => {
        const { key, ...rest } = getTagProps({ index: i });
        return <Chip key={key} size="small" label={t} {...rest} />;
      })}
      renderInput={(params) => (
        <TextField
          {...params}
          data-help="store.serialField.input"
          label={label ?? (mode === 'pick' ? 'Serial numbers (pick)' : 'Serial numbers')}
          placeholder={count === 0 ? (mode === 'pick' ? 'Pick or scan' : 'Scan, type + Enter, or paste a list') : undefined}
          error={mismatch}
          helperText={helper}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\n,;]/.test(text)) { e.preventDefault(); add(parseSerials(text)); }
          }}
          inputProps={{ ...params.inputProps, 'data-testid': testId }}
        />
      )}
    />
  );
};

export default SerialNumbersField;
